import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { RefreshCw, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

export default function ContentEnhancementFix() {
  const [isFixing, setIsFixing] = useState(false);
  const [fixResult, setFixResult] = useState<any>(null);
  const [progress, setProgress] = useState<any>(null);
  const [isMonitoring, setIsMonitoring] = useState(false);

  const handleFix = async () => {
    setIsFixing(true);
    setFixResult(null);
    
    try {
      toast.info('Starting content enhancement system fix...');
      
      const { data, error } = await supabase.functions.invoke('fix-content-enhancement', {
        body: {}
      });

      if (error) throw error;

      setFixResult(data);
      
      if (data.success) {
        toast.success('Enhancement system fixed successfully!');
        
        // Start monitoring progress
        if (data.operationId) {
          setIsMonitoring(true);
          monitorProgress(data.operationId);
        }
      } else {
        toast.error('Failed to fix enhancement system');
      }

    } catch (error: any) {
      console.error('Error fixing enhancement:', error);
      toast.error(`Failed to fix system: ${error.message}`);
    } finally {
      setIsFixing(false);
    }
  };

  const monitorProgress = async (operationId: string) => {
    const interval = setInterval(async () => {
      try {
        const { data, error } = await supabase
          .from('content_enhancement_operations')
          .select('*')
          .eq('id', operationId)
          .single();

        if (error) {
          console.error('Error fetching progress:', error);
          return;
        }

        if (data) {
          setProgress(data);
          
          if (data.status === 'completed') {
            clearInterval(interval);
            setIsMonitoring(false);
            toast.success('Content enhancement completed!');
          } else if (data.status === 'failed') {
            clearInterval(interval);
            setIsMonitoring(false);
            toast.error('Enhancement failed. Please check logs.');
          }
        }
      } catch (error) {
        console.error('Error monitoring progress:', error);
      }
    }, 3000); // Check every 3 seconds

    // Cleanup on unmount
    return () => clearInterval(interval);
  };

  const progressPercentage = progress 
    ? Math.round((progress.processed_items / progress.total_items) * 100)
    : 0;

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Content Enhancement System Fix</h1>
        <p className="text-muted-foreground">
          Emergency fix to restart content enhancement for all modules with &lt; 200 words
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5" />
            System Fix
          </CardTitle>
          <CardDescription>
            This will clear all stuck operations and restart content enhancement for 389 modules
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted p-4 rounded-lg space-y-2">
            <h3 className="font-semibold">What this will do:</h3>
            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
              <li>Clear all stuck "in_progress" operations</li>
              <li>Clear all stuck module processing statuses</li>
              <li>Find all modules with &lt; 200 words (173 placeholders + 216 short content)</li>
              <li>Start fresh bulk enhancement to expand all to 1000+ words</li>
              <li>Use AI to generate comprehensive content with examples and best practices</li>
            </ul>
          </div>

          <Button
            onClick={handleFix}
            disabled={isFixing || isMonitoring}
            className="w-full"
            size="lg"
          >
            {isFixing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Fixing System...
              </>
            ) : isMonitoring ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enhancement Running...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Fix & Restart Enhancement
              </>
            )}
          </Button>

          {fixResult && (
            <Card className={fixResult.success ? 'border-green-500' : 'border-red-500'}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  {fixResult.success ? (
                    <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                  )}
                  <div className="space-y-2 flex-1">
                    <p className="font-semibold">{fixResult.message}</p>
                    {fixResult.modulesNeedingEnhancement && (
                      <p className="text-sm text-muted-foreground">
                        Modules needing enhancement: {fixResult.modulesNeedingEnhancement}
                      </p>
                    )}
                    {fixResult.operationId && (
                      <p className="text-xs font-mono text-muted-foreground">
                        Operation ID: {fixResult.operationId}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {progress && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Enhancement Progress</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Progress</span>
                    <span className="font-semibold">{progressPercentage}%</span>
                  </div>
                  <Progress value={progressPercentage} className="h-2" />
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Total Modules</p>
                    <p className="text-2xl font-bold">{progress.total_items}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Processed</p>
                    <p className="text-2xl font-bold">{progress.processed_items}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Successful</p>
                    <p className="text-2xl font-bold text-green-600">{progress.successful_items}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Failed</p>
                    <p className="text-2xl font-bold text-red-600">{progress.failed_items}</p>
                  </div>
                </div>

                <div className="pt-2 border-t">
                  <p className="text-xs text-muted-foreground">
                    Status: <span className="font-semibold">{progress.status}</span>
                  </p>
                  {progress.error_message && (
                    <p className="text-xs text-red-600 mt-1">{progress.error_message}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Current System Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Placeholder Content (&lt; 10 words)</span>
              <span className="font-semibold">173 modules</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Short Content (10-199 words)</span>
              <span className="font-semibold">216 modules</span>
            </div>
            <div className="flex justify-between pt-2 border-t">
              <span className="font-semibold">Total Needing Enhancement</span>
              <span className="font-bold text-lg">389 modules</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
